import {
  getStoresApi,
  getStoreApi,
  createStoreApi,
  updateStoreApi,
  updateRefreshTokenApi,
  getStoresListApi,
  uploadDescriptionTemplateApi,
  deleteStoreApi,
  loadListingsApi,
  getStorePoliciesApi,
  refreshStorePoliciesApi,
  updateStoreDefaultPoliciesApi,
  getPoliciesRefreshStatusApi,
  getPoliciesProcessStatusApi,
  startPoliciesBulkUpdateApi,
  stopPoliciesBulkUpdateApi,
  downloadPoliciesRunReportCsvApi,
  getLoadListingsStatusApi,
  updateLoadListingsScheduleApi,
  downloadLoadListingsRunReportCsvApi,
} from './stores.api';

import { ToastUtil } from '../utils/toast.util';

export const getStoresReducers = (builder) => {
  builder
    .addCase(getStoresApi.pending, (state) => {
      state.getStoresStatus = 'pending';
    })
    .addCase(getStoresApi.fulfilled, (state, { payload }) => {
      state.getStoresStatus = 'fulfilled';
      state.stores = payload.stores;
      state.totalStores = payload.totalCount;
      state.totalPages = payload.totalPages;
      state.currentPage = payload.currentPage;
    })
    .addCase(getStoresApi.rejected, (state, action) => {
      state.getStoresStatus = 'failed';
      state.error = action?.error?.message;
    });
};

export const getStoreReducers = (builder) => {
  builder
    .addCase(getStoreApi.pending, (state) => {
      state.getStoreStatus = 'pending';
      state.error = null;
    })
    .addCase(getStoreApi.fulfilled, (state, action) => {
      state.getStoreStatus = 'fulfilled';
      state.store = action.payload;
      state.error = null;
    })
    .addCase(getStoreApi.rejected, (state, action) => {
      state.getStoreStatus = 'rejected';
      state.error = action?.error?.message;
    });
};

export const createStoreReducers = (builder) => {
  builder
    .addCase(createStoreApi.pending, (state) => {
      state.createStoreStatus = 'pending';
      state.error = null;
    })
    .addCase(createStoreApi.fulfilled, (state, { payload }) => {
      state.createStoreStatus = 'fulfilled';

      if (payload.success) state.stores = [...state.stores, payload.data];
      state.error = payload.message;

      ToastUtil.handleApiResponse(
        payload,
        'Store created successfully',
        'Store creation failed',
      );
    })
    .addCase(createStoreApi.rejected, (state, action) => {
      state.createStoreStatus = 'rejected';
      state.error = action?.error?.message;
      ToastUtil.error(
        'Store creation failed',
        action?.error?.message || 'Failed to create store',
      );
    });
};

export const updateStoreReducers = (builder) => {
  builder
    .addCase(updateStoreApi.pending, (state) => {
      state.updateStoreStatus = 'pending';
      state.error = null;
    })
    .addCase(updateStoreApi.fulfilled, (state, { payload }) => {
      state.updateStoreStatus = 'fulfilled';

      if (payload.success) {
        state.store = payload.data;
      }
      state.error = payload.message;

      ToastUtil.handleApiResponse(
        payload,
        'Store updated successfully',
        'Store update failed',
      );
    })
    .addCase(updateStoreApi.rejected, (state, action) => {
      state.updateStoreStatus = 'rejected';
      state.error = action?.error?.message;
      ToastUtil.error(
        'Store update failed',
        action?.error?.message || 'Failed to update store',
      );
    });
};

export const getStoresListReducers = (builder) => {
  builder
    .addCase(getStoresListApi.pending, (state) => {
      state.getStoresListStatus = 'pending';
    })
    .addCase(getStoresListApi.fulfilled, (state, { payload }) => {
      state.getStoresListStatus = 'fulfilled';
      state.list = payload;
    })
    .addCase(getStoresListApi.rejected, (state, action) => {
      state.getStoresListStatus = 'failed';
      state.error = action?.error?.message;
    });
};

export const updateRefreshTokenReducers = (builder) => {
  builder
    .addCase(updateRefreshTokenApi.pending, (state) => {
      state.updateRefreshTokenStatus = 'pending';
      state.error = null;
    })
    .addCase(updateRefreshTokenApi.fulfilled, (state, { payload }) => {
      state.updateRefreshTokenStatus = 'fulfilled';
      state.error = payload.message;

      ToastUtil.handleApiResponse(
        payload,
        'Refresh token updated successfully',
        'Refresh token update failed',
      );
    })
    .addCase(updateRefreshTokenApi.rejected, (state, action) => {
      state.updateRefreshTokenStatus = 'rejected';
      state.error = action?.error?.message;
      ToastUtil.error(
        'Refresh token update failed',
        action?.error?.message || 'Failed to update refresh token',
      );
    });
};

export const uploadDescriptionTemplateReducers = (builder) => {
  builder
    .addCase(uploadDescriptionTemplateApi.pending, (state) => {
      state.uploadDescriptionTemplateStatus = 'pending';
      state.error = null;
    })
    .addCase(uploadDescriptionTemplateApi.fulfilled, (state, { payload }) => {
      state.uploadDescriptionTemplateStatus = 'fulfilled';

      if (payload.success) {
        if (state.store && state.store._id === payload.data.storeId) {
          state.store = { ...state.store, descriptionTemplate: true };
        }
        state.stores = state.stores.map((store) =>
          store._id === payload.data.storeId
            ? { ...store, descriptionTemplate: true }
            : store,
        );
      }
      state.error = payload.message;

      ToastUtil.handleApiResponse(
        payload,
        'Description template uploaded successfully',
        'Description template upload failed',
      );
    })
    .addCase(uploadDescriptionTemplateApi.rejected, (state, action) => {
      state.uploadDescriptionTemplateStatus = 'rejected';
      state.error = action?.error?.message;
      ToastUtil.error(
        'Description template upload failed',
        action?.error?.message || 'Failed to upload description template',
      );
    });
};

export const deleteStoreReducers = (builder) => {
  builder
    .addCase(deleteStoreApi.pending, (state) => {
      state.deleteStoreStatus = 'pending';
      state.error = null;
    })
    .addCase(deleteStoreApi.fulfilled, (state, action) => {
      state.deleteStoreStatus = 'fulfilled';
      const { payload } = action;
      const deletedStoreId = action.meta.arg;

      if (payload.success) {
        state.stores = state.stores.filter((store) => store._id !== deletedStoreId);
        state.totalStores = Math.max(0, state.totalStores - 1);
        if (state.store && state.store._id === deletedStoreId) {
          state.store = null;
        }
      }
      state.error = payload.message;

      ToastUtil.handleApiResponse(
        payload,
        'Store deleted successfully',
        'Store deletion failed',
      );
    })
    .addCase(deleteStoreApi.rejected, (state, action) => {
      state.deleteStoreStatus = 'rejected';
      state.error = action?.error?.message;
      ToastUtil.error(
        'Store deletion failed',
        action?.error?.message || 'Failed to delete store',
      );
    });
};

export const loadListingsReducers = (builder) => {
  builder
    .addCase(loadListingsApi.pending, (state) => {
      state.loadListingsStatus = 'pending';
      state.error = null;
    })
    .addCase(loadListingsApi.fulfilled, (state, { payload }) => {
      state.loadListingsStatus = 'fulfilled';
      state.error = payload.message;
      state.isLoadListingsRunning = true;

      ToastUtil.handleApiResponse(
        payload,
        'Listings loading started successfully',
        'Failed to start listings loading',
      );
    })
    .addCase(loadListingsApi.rejected, (state, action) => {
      state.loadListingsStatus = 'rejected';
      state.error = action?.error?.message;
      ToastUtil.error(
        'Failed to start listings loading',
        action?.error?.message || 'Failed to load listings',
      );
    });
};

export const storePoliciesReducers = (builder) => {
  builder
    .addCase(getStorePoliciesApi.pending, (state) => {
      state.getStorePoliciesStatus = 'pending';
    })
    .addCase(getStorePoliciesApi.fulfilled, (state, { payload }) => {
      state.getStorePoliciesStatus = 'fulfilled';
      state.storePolicies = payload;
    })
    .addCase(getStorePoliciesApi.rejected, (state) => {
      state.getStorePoliciesStatus = 'rejected';
    })
    .addCase(refreshStorePoliciesApi.pending, (state) => {
      state.refreshStorePoliciesStatus = 'pending';
    })
    .addCase(refreshStorePoliciesApi.fulfilled, (state, { payload }) => {
      state.refreshStorePoliciesStatus = 'fulfilled';
      if (payload?.refreshStatus) {
        state.policiesRefreshStatus = payload.refreshStatus;
        state.isPoliciesRefreshing = Boolean(payload.refreshStatus?.isRunning);
      }
      ToastUtil.success('Policy refresh started', 'Fetching policies and listing counts from eBay');
    })
    .addCase(refreshStorePoliciesApi.rejected, (state, action) => {
      state.refreshStorePoliciesStatus = 'rejected';
      ToastUtil.error('Refresh failed', action.payload?.message || 'Could not refresh policies');
    })
    .addCase(getPoliciesRefreshStatusApi.fulfilled, (state, { payload }) => {
      state.policiesRefreshStatus = payload;
      state.isPoliciesRefreshing = Boolean(payload?.isRunning);
      if (!payload?.isRunning && payload?.overallProgress === 100) {
        // Refresh finished while user was away — policies snapshot will be reloaded by tab effect
      }
    })
    .addCase(updateStoreDefaultPoliciesApi.pending, (state) => {
      state.updateDefaultPoliciesStatus = 'pending';
    })
    .addCase(updateStoreDefaultPoliciesApi.fulfilled, (state, { payload }) => {
      state.updateDefaultPoliciesStatus = 'fulfilled';
      if (state.storePolicies && payload?.defaultPolicies) {
        state.storePolicies.defaultPolicies = payload.defaultPolicies;
      }
      ToastUtil.success('Default policies saved', 'These policies will be used for new listings and other store flows');
    })
    .addCase(updateStoreDefaultPoliciesApi.rejected, (state, action) => {
      state.updateDefaultPoliciesStatus = 'rejected';
      ToastUtil.error('Save failed', action.payload?.message || 'Could not save default policies');
    })
    .addCase(getPoliciesProcessStatusApi.fulfilled, (state, { payload }) => {
      state.policiesProcessStatus = payload;
      state.isPoliciesRunning = Boolean(payload?.isRunning);
    })
    .addCase(startPoliciesBulkUpdateApi.fulfilled, (state) => {
      state.isPoliciesRunning = true;
    })
    .addCase(downloadPoliciesRunReportCsvApi.pending, (state) => {
      state.downloadPoliciesReportStatus = 'loading';
    })
    .addCase(downloadPoliciesRunReportCsvApi.fulfilled, (state) => {
      state.downloadPoliciesReportStatus = 'succeeded';
    })
    .addCase(downloadPoliciesRunReportCsvApi.rejected, (state) => {
      state.downloadPoliciesReportStatus = 'failed';
    });
};

export const loadListingsProgressReducers = (builder) => {
  builder
    .addCase(getLoadListingsStatusApi.fulfilled, (state, { payload }) => {
      state.loadListingsProcessStatus = payload;
      state.isLoadListingsRunning = Boolean(payload?.isRunning);
    })
    .addCase(updateLoadListingsScheduleApi.fulfilled, (state, { payload }) => {
      state.updateLoadListingsScheduleStatus = 'fulfilled';
      if (state.store) {
        state.store = { ...state.store, listingFetchSchedule: payload };
      }
      ToastUtil.success('Schedule saved', 'Load listings schedule updated');
    })
    .addCase(updateLoadListingsScheduleApi.rejected, (state, action) => {
      state.updateLoadListingsScheduleStatus = 'rejected';
      ToastUtil.error('Save failed', action.payload?.message || 'Could not save schedule');
    })
    .addCase(downloadLoadListingsRunReportCsvApi.pending, (state) => {
      state.downloadLoadListingsReportStatus = 'loading';
    })
    .addCase(downloadLoadListingsRunReportCsvApi.fulfilled, (state) => {
      state.downloadLoadListingsReportStatus = 'succeeded';
    })
    .addCase(downloadLoadListingsRunReportCsvApi.rejected, (state) => {
      state.downloadLoadListingsReportStatus = 'failed';
    });
};
